import React, { useState } from "react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Film, Plus, X, Upload, Link2, Package, Clock, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const VIDEO_CHUNK = 4 * 1024 * 1024;
async function uploadVideoChunked(file, onProgress) {
  const init = await api.post("/uploads/video/init", { filename: file.name, content_type: file.type || "video/mp4" });
  const uid = init.data.upload_id;
  let sent = 0;
  for (let start = 0; start < file.size; start += VIDEO_CHUNK) {
    const chunk = file.slice(start, Math.min(start + VIDEO_CHUNK, file.size));
    const buf = await chunk.arrayBuffer();
    await api.put(`/uploads/video/${uid}`, buf, { headers: { "Content-Type": "application/octet-stream" } });
    sent += chunk.size;
    onProgress?.(Math.round((sent / file.size) * 100));
  }
  const done = await api.post(`/uploads/video/${uid}/complete`);
  return done.data;
}
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: "Menunggu", cls: "bg-showty-peach text-showty-navy border-showty-gold/40", icon: Clock },
    manager_approved: { label: "Approved Manager", cls: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle2 },
    approved: { label: "Final Approved", cls: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle2 },
    rejected: { label: "Ditolak", cls: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${s.cls}`} data-testid={`status-${status}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </Badge>
  );
};

export const ProductCard = ({ product, onRequest, requesting }) => (
  <motion.div whileHover={{ y: -4 }} className="rounded-2xl border border-border bg-white overflow-hidden flex flex-col" data-testid={`product-${product.id}`}>
    <div className="h-40 overflow-hidden bg-showty-pink/30">
      {product.image_url ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-showty-red/40" /></div>}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <h3 className="font-display text-xl text-showty-navy">{product.name}</h3>
      <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{product.description}</p>
      <div className="mt-3 rounded-lg bg-showty-cream border border-border px-3 py-2">
        <p className="text-[11px] uppercase tracking-wide text-showty-gold font-semibold">Barter</p>
        <p className="text-xs text-showty-navy mt-0.5">{product.barter_terms}</p>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Stok: {product.stock}</span>
        <Button size="sm" onClick={() => onRequest(product)} disabled={requesting} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid={`request-btn-${product.id}`}>
          Request Sample
        </Button>
      </div>
    </div>
  </motion.div>
);

export const SubmitVideoDialog = ({ role, onDone, defaultProduct }) => {
  const [open, setOpen] = useState(false);
  const [productName, setProductName] = useState(defaultProduct || "");
  const [platform, setPlatform] = useState("");
  const [saving, setSaving] = useState(false);
  const [videoFiles, setVideoFiles] = useState([]);
  const [uploadPct, setUploadPct] = useState(null);

  const minVideos = role === "beauty_creator" ? 2 : 1;
  const requirement = role === "beauty_creator"
    ? "Wajib min. 2 video (TikTok + Instagram owning content)."
    : "Wajib 1 video TikTok + Live di akun affiliate kamu.";

  const reset = () => { setProductName(defaultProduct || ""); setPlatform(""); setVideoFiles([]); setUploadPct(null); };

  const onPickVideos = async (fileList) => {
    const arr = Array.from(fileList || []);
    for (const file of arr) {
      if (!file.type.startsWith("video/")) { toast.error(`${file.name} bukan file video`); continue; }
      try {
        setUploadPct(0);
        const meta = await uploadVideoChunked(file, setUploadPct);
        setVideoFiles((prev) => [...prev, meta]);
        toast.success(`Video "${meta.name}" terupload`);
      } catch (e) {
        toast.error(`Gagal upload ${file.name}`);
      } finally {
        setUploadPct(null);
      }
    }
  };

  const submit = async () => {
    if (!productName.trim()) return toast.error("Isi nama produk");
    if (uploadPct !== null) return toast.error("Tunggu upload video selesai");
    if (videoFiles.length < minVideos) return toast.error(`Wajib upload minimal ${minVideos} video`);
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("product_name", productName);
      fd.append("submission_type", "content");
      fd.append("platform", platform);
      fd.append("video_links", JSON.stringify([]));
      fd.append("video_files", JSON.stringify(videoFiles));
      await api.post("/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video berhasil disubmit, menunggu approval");
      reset();
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal submit video");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-showty-navy hover:bg-showty-navy/90 text-white" data-testid="open-submit-video">
          <Plus className="w-4 h-4 mr-1.5" /> Submit Video
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="submit-video-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-showty-navy">Submit Konten Video</DialogTitle>
          <DialogDescription>{requirement}</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-showty-cream border border-showty-gold/40 px-4 py-3 mt-1 flex gap-2" data-testid="submit-video-note">
          <Link2 className="w-4 h-4 text-showty-red shrink-0 mt-0.5" />
          <p className="text-xs text-showty-navy">Upload <b>file video HD</b> kamu (boleh lebih dari satu). Manager bisa download versi aslinya untuk review & arsip.</p>
        </div>
        <div className="space-y-4 py-2">
          <div>
            <Label>Nama produk</Label>
            <Input className="mt-1.5" placeholder="Showty Velvet Lip Tint" value={productName} onChange={(e) => setProductName(e.target.value)} data-testid="submit-product-name" />
          </div>
          <div>
            <Label>Platform</Label>
            <Input className="mt-1.5" placeholder="TikTok / Instagram" value={platform} onChange={(e) => setPlatform(e.target.value)} data-testid="submit-platform" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Film className="w-3.5 h-3.5" /> Upload Video HD</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">Unggah file video kualitas HD (boleh lebih dari satu). Manager bisa download versi aslinya.</p>
            <label className="mt-1.5 flex items-center gap-2 rounded-lg border border-dashed border-border bg-showty-cream/40 px-4 py-3 text-sm cursor-pointer hover:bg-showty-pink/20" data-testid="submit-video-file-label">
              <Upload className="w-4 h-4 text-showty-red" />
              <span className="truncate">{uploadPct !== null ? `Mengupload... ${uploadPct}%` : "Klik untuk pilih file video (bisa lebih dari satu)"}</span>
              <input type="file" accept="video/*" multiple className="hidden" disabled={uploadPct !== null} onChange={(e) => onPickVideos(e.target.files)} data-testid="submit-video-file-input" />
            </label>
            {uploadPct !== null && (
              <div className="mt-2 h-1.5 rounded-full bg-showty-pink/40 overflow-hidden"><div className="h-full bg-showty-red transition-all" style={{ width: `${uploadPct}%` }} /></div>
            )}
            {videoFiles.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="submit-video-files-list">
                {videoFiles.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-xs" data-testid={`submit-video-file-${i}`}>
                    <Film className="w-3.5 h-3.5 text-showty-navy shrink-0" />
                    <span className="flex-1 truncate text-showty-navy">{v.name}</span>
                    <span className="text-muted-foreground">{fmtSize(v.size)}</span>
                    <button type="button" onClick={() => setVideoFiles(videoFiles.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`submit-video-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="submit-video-confirm">
            {saving ? "Mengirim…" : "Kirim untuk Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RequestSampleNote = ({ product, onConfirm }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const go = async () => {
    setSaving(true);
    await onConfirm(product, note);
    setSaving(false);
    setOpen(false);
    setNote("");
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid={`request-btn-${product.id}`}>Request Sample</Button>
      </DialogTrigger>
      <DialogContent data-testid="request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-showty-navy">Request {product.name}</DialogTitle>
          <DialogDescription>Barter: {product.barter_terms}</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label>Catatan (opsional)</Label>
          <Textarea className="mt-1.5" placeholder="Ceritakan ide konten kamu…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="request-note" />
        </div>
        <DialogFooter>
          <Button onClick={go} disabled={saving} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="request-confirm">
            {saving ? "Mengirim…" : "Kirim Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
